import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

/**
 * Hook to monitor session validity and handle auth errors globally
 */
export const useSessionMonitor = () => {
  const { user, session, refreshSession } = useAuth();
  const lastCheckRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Listen for user activity
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [updateActivity]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        // Only check if we think we have a session
        if (!session) return;

        // Check if user has been inactive for more than 30 minutes
        const inactiveTime = Date.now() - lastActivityRef.current;
        const thirtyMinutes = 30 * 60 * 1000;
        
        if (inactiveTime > thirtyMinutes) {
          console.log('User inactive for 30 minutes, checking session...');
        }

        // Check if session is still valid
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error || !currentSession) {
          console.error('Session check failed:', error);
          // Force logout on session error
          await supabase.auth.signOut();
          window.location.href = '/login';
          return;
        }

        // Check if session is about to expire (within 10 minutes)
        if (currentSession.expires_at) {
          const expiresAt = currentSession.expires_at * 1000;
          const timeUntilExpiry = expiresAt - Date.now();
          
          // Refresh session if less than 10 minutes remaining and user is active
          if (timeUntilExpiry < 10 * 60 * 1000 && inactiveTime < thirtyMinutes) {
            console.log('Session expiring soon and user is active, refreshing...');
            await refreshSession();
          }
        }

        lastCheckRef.current = Date.now();
      } catch (error) {
        console.error('Session monitor error:', error);
        // On any error, try to refresh the session
        try {
          await refreshSession();
        } catch (refreshError) {
          console.error('Failed to recover session:', refreshError);
        }
      }
    };

    // Check session every 1 minute for more responsive handling
    checkIntervalRef.current = setInterval(checkSession, 60 * 1000);

    // Initial check
    checkSession();

    // Listen for visibility changes - check session when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && Date.now() - lastCheckRef.current > 60 * 1000) {
        checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for online/offline events
    const handleOnline = () => {
      console.log('Network connection restored, checking session...');
      checkSession();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      if (checkIntervalRef.current !== null) {
        clearInterval(checkIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [session]);

  return { user, session };
};
